/* MediVita theme scripts — no dependencies. */
(() => {
  const P = window.MediVita || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------------- Money ---------------- */
  function formatMoney(cents, format = P.moneyFormat || '{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands, dec) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${dec}${frac}` : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2, ',', '.');
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2, ',', '.');
      }
    });
  }

  /* ---------------- Mobile menu ---------------- */
  const menu = $('#MobileMenu');
  const menuBtn = $('[data-menu-open]');
  if (menu && menuBtn) {
    const setMenu = (open) => {
      menu.hidden = !open;
      menuBtn.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    };
    menuBtn.addEventListener('click', () => setMenu(true));
    $$('[data-menu-close], #MobileMenu a', menu).forEach((el) => el.addEventListener('click', () => setMenu(false)));
  }

  /* ---------------- Cart drawer ---------------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.lastFocus = null;
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-input]');
        if (input) this.changeLine(input.dataset.lineInput, Math.max(0, parseInt(input.value, 10) || 0));
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener) { e.preventDefault(); this.open(); }
      });
    }

    onClick(e) {
      if (e.target.closest('[data-cart-close]')) return this.close();
      const remove = e.target.closest('[data-line-remove]');
      if (remove) return this.changeLine(remove.dataset.lineRemove, 0);
      const step = e.target.closest('[data-qty-change]');
      if (step) {
        const input = $('input', step.closest('[data-qty]'));
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyChange));
        this.changeLine(input.dataset.lineInput, next);
      }
    }

    open() {
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => $('.drawer__panel', this).focus({ preventScroll: true }), 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    }

    async changeLine(line, quantity) {
      this.classList.add('is-loading');
      try {
        const res = await fetch(`${P.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: 'cart-drawer', sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message);
        this.render(data.sections, data.item_count);
      } catch (err) {
        console.error(err);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(sections, count) {
      if (sections && sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = doc.querySelector('#CartDrawerInner');
        if (fresh) $('#CartDrawerInner', this).replaceWith(fresh);
      }
      if (typeof count === 'number') updateCount(count);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* Adds items ([{ id, quantity }]) and opens the drawer. Throws on error. */
  async function addToCart(items) {
    const res = await fetch(`${P.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || P.strings.error);
    if (window.location.pathname === P.routes.cart) { window.location.reload(); return; }
    const drawer = $('cart-drawer');
    const cart = await fetch(`${P.routes.cart}.js`).then((r) => r.json());
    if (drawer) { drawer.render(data.sections, cart.item_count); drawer.open(); }
    else window.location.href = P.routes.cart;
  }

  /* Quick add: product cards and cart drawer upsells. */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = P.strings.adding;
    try {
      await addToCart([{ id: Number(btn.dataset.quickAdd), quantity: 1 }]);
    } catch (err) {
      window.alert(err.message || P.strings.error);
    } finally {
      if (btn.isConnected) { btn.disabled = false; btn.textContent = label; }
    }
  });

  /* ---------------- Product page ---------------- */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      this.data = json ? JSON.parse(json.textContent) : { variants: [] };
      this.form = $('[data-product-form]', this);
      this.section = this.closest('section');

      const stockJson = $('[data-stock-json]', this);
      this.stock = stockJson ? JSON.parse(stockJson.textContent) : {};
      this.variant = this.data.variants.find((v) => String(v.id) === String(($('[data-variant-id]', this) || {}).value)) || this.data.variants[0];

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-index]')) this.onOptionChange();
        if (e.target.matches('[data-qb-qty]')) this.onQtyBreakChange();
        if (e.target.matches('[data-upsell]')) e.target.closest('.fbt__item').classList.toggle('is-selected', e.target.checked);
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.initGallery();
      this.initSticky();
      this.onQtyBreakChange();
    }

    /* Total for the selected quantity tier (or 1 piece). */
    currentTotal() {
      const v = this.variant;
      if (!v) return 0;
      const picked = $('[data-qb-qty]:checked', this);
      if (!picked) return v.price;
      if (picked.dataset.fixedTotal) return Number(picked.dataset.fixedTotal);
      const full = v.price * Number(picked.value);
      return Math.floor((full * (100 - Number(picked.dataset.discount || 0))) / 100);
    }

    updateLabel() {
      const btn = $('[data-add-button]', this);
      if (!btn || !this.variant || !this.variant.available) return;
      const base = btn.dataset.label || P.strings.addToCart;
      const label = btn.dataset.showPrice === 'true'
        ? P.strings.addToCartFor.replace('__L__', base).replace('__P__', formatMoney(this.currentTotal()))
        : base;
      $('[data-add-label]', btn).textContent = label;
    }

    updatePrices(v) {
      // Omnibus: bez metapola custom.lowest_price_30d nie pokazujemy przekreślonej ceny.
      const history = this.data.priceHistory && this.data.priceHistory[String(v.id)];
      const sale = !!history && v.compare_at_price && v.compare_at_price > v.price;
      const saveText = sale ? P.strings.youSave.replace('__A__', formatMoney(v.compare_at_price - v.price)) : '';
      const set = (sel, text, show, root = this) => {
        const el = $(sel, root);
        if (!el) return;
        if (text !== null) el.textContent = text;
        if (show !== null) el.hidden = !show;
      };
      set('[data-price-now]', formatMoney(v.price), null);
      set('[data-price-was]', sale ? formatMoney(v.compare_at_price) : '', sale);
      set('[data-price-save-wrap]', null, sale);
      set('[data-price-save]', saveText, null);
      set('[data-price-percent]', sale ? '−' + Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '%' : '', sale);
      set('[data-omnibus]', history ? history.label : '', sale);
      const inst = $('[data-installments]', this);
      if (inst) set('[data-installment]', formatMoney(Math.floor(v.price / Number(inst.dataset.installments))), null, inst);

      const bar = this.section && $('[data-sticky-atc]', this.section);
      if (bar) {
        set('[data-sticky-price]', formatMoney(v.price), null, bar);
        set('[data-sticky-was]', sale ? formatMoney(v.compare_at_price) : '', sale, bar);
        set('[data-sticky-save]', saveText, sale, bar);
        const select = $('[data-sticky-variant]', bar);
        if (select) select.value = String(v.id);
      }
    }

    /* Selects a variant by id (used by the sticky bar dropdown). */
    selectVariant(id) {
      const v = this.data.variants.find((x) => String(x.id) === String(id));
      if (!v) return;
      v.options.forEach((value, i) => {
        $$(`[data-option-index="${i}"]`, this).forEach((input) => { input.checked = input.value === value; });
      });
      this.onOptionChange();
    }

    onQtyBreakChange() {
      const picked = $('[data-qb-qty]:checked', this);
      $$('.qb__opt', this).forEach((label) => label.classList.toggle('is-selected', $('input', label).checked));
      const qty = $('[data-quantity]', this);
      if (qty) qty.value = picked ? picked.value : 1;
      this.updateLabel();
    }

    updateQtyBreaks(variant) {
      $$('[data-qb-qty]', this).forEach((input) => {
        if (input.dataset.fixedTotal) return;
        const label = input.closest('.qb__opt');
        const full = variant.price * Number(input.value);
        const total = Math.floor((full * (100 - Number(input.dataset.discount || 0))) / 100);
        const totalEl = $('[data-qb-total]', label);
        const fullEl = $('[data-qb-full]', label);
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (fullEl) fullEl.textContent = formatMoney(full);
      });
    }

    updateStock(variant) {
      const low = $('[data-stock-low]', this);
      if (!low) return;
      const n = this.stock[variant.id];
      const text = $('[data-stock-text]', low);
      if (n && variant.available) {
        const form = new Intl.PluralRules(document.documentElement.lang || 'pl').select(n);
        const tpl = text.dataset[form] || text.dataset.many || text.dataset.other || '__N__';
        text.textContent = tpl.replace('__N__', n);
      }
      low.hidden = !(n && variant.available);
      const ok = $('[data-stock-ok]', this);
      if (ok) ok.hidden = !low.hidden || !variant.available;
    }

    selectedOptions() {
      const opts = [];
      $$('[data-option-index]:checked', this).forEach((input) => { opts[Number(input.dataset.optionIndex)] = input.value; });
      return opts;
    }

    onOptionChange() {
      const opts = this.selectedOptions();
      const variant = this.data.variants.find((v) => v.options.every((o, i) => opts[i] === undefined || o === opts[i]));

      $$('.bundle, .pill', this).forEach((label) => {
        const input = $('input', label);
        label.classList.toggle('is-selected', input.checked);
      });
      opts.forEach((val, i) => {
        const out = $(`[data-option-value="${i}"]`, this);
        if (out) out.textContent = val;
      });
      if (!variant) return this.setButton(false, P.strings.soldOut);

      this.variant = variant;
      const idInput = $('[data-variant-id]', this);
      if (idInput) idInput.value = variant.id;

      this.updatePrices(variant);
      this.updateQtyBreaks(variant);
      this.updateStock(variant);

      this.setButton(variant.available, variant.available ? P.strings.addToCart : P.strings.soldOut);
      this.updateLabel();

      if (variant.featured_media) this.showMedia(variant.featured_media.id);

      if (this.closest('.section-main-product') || document.body.classList.contains('template-product')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    setButton(enabled, label) {
      const btn = $('[data-add-button]', this);
      if (!btn) return;
      btn.disabled = !enabled;
      $('[data-add-label]', btn).textContent = label;
    }

    async onSubmit(e) {
      e.preventDefault();
      const btn = $('[data-add-button]', this);
      const labelEl = $('[data-add-label]', btn);
      const label = labelEl.textContent;
      const err = $('[data-form-error]', this);
      err.hidden = true;
      btn.disabled = true;
      labelEl.textContent = P.strings.adding;

      const id = Number($('[data-variant-id]', this).value);
      const quantity = Number(($('[data-quantity]', this) || {}).value) || 1;
      const items = [{ id, quantity }];
      $$('[data-upsell]:checked', this).forEach((box) => items.push({ id: Number(box.value), quantity: 1 }));

      try {
        await addToCart(items);
      } catch (error) {
        err.textContent = error.message || P.strings.error;
        err.hidden = false;
      } finally {
        btn.disabled = false;
        labelEl.textContent = label;
      }
    }

    initGallery() {
      this.track = $('[data-gallery-track]', this);
      this.thumbs = $$('[data-thumb]', this);
      if (!this.track) return;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.showMedia(t.dataset.thumb)));
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: 'smooth' });
      const prev = $('[data-gallery-prev]', this);
      const next = $('[data-gallery-next]', this);
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));

      const slides = $$('.gallery__slide', this.track);
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.6) return;
          const id = entry.target.dataset.mediaId;
          this.thumbs.forEach((t) => {
            const on = t.dataset.thumb === id;
            t.classList.toggle('is-active', on);
            if (on) t.parentElement.scrollTo({ top: t.offsetTop - 8, left: t.offsetLeft - 8, behavior: 'smooth' });
          });
        });
      }, { root: this.track, threshold: [0.6] });
      slides.forEach((s) => io.observe(s));
    }

    showMedia(id) {
      const slide = this.track && $(`[data-media-id="${id}"]`, this.track);
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    initSticky() {
      const bar = this.section && $('[data-sticky-atc]', this.section);
      const btn = $('[data-add-button]', this);
      if (!bar || !btn) return;
      const io = new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting;
      });
      io.observe(btn);
      const select = $('[data-sticky-variant]', bar);
      if (select) select.addEventListener('change', () => this.selectVariant(select.value));
      $('[data-sticky-submit]', bar).addEventListener('click', () => {
        if (this.form.requestSubmit) this.form.requestSubmit(); else btn.click();
      });
    }
  }
  customElements.define('product-page', ProductPage);

  /* ---------------- Frequently bought together ---------------- */
  class ProductBundle extends HTMLElement {
    connectedCallback() {
      this.button = $('[data-bundle-add]', this);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-bundle-item]')) e.target.closest('.bundle__item').classList.toggle('is-selected', e.target.checked);
        this.update();
      });
      this.button.addEventListener('click', () => this.add());
      this.update();
    }

    main() {
      const el = $('[data-bundle-main]', this);
      const price = el.tagName === 'SELECT' ? el.selectedOptions[0].dataset.price : el.dataset.price;
      return { id: Number(el.value), price: Number(price) };
    }

    update() {
      const main = this.main();
      const boxes = $$('[data-bundle-item]:checked', this);
      const total = boxes.reduce((sum, box) => sum + Number(box.dataset.price), main.price);
      $('[data-bundle-main-price]', this).textContent = formatMoney(main.price);
      $('[data-bundle-total]', this).textContent = formatMoney(total);
      this.button.textContent = boxes.length ? this.button.dataset.label : P.strings.addToCart;
    }

    async add() {
      const items = [{ id: this.main().id, quantity: 1 }];
      $$('[data-bundle-item]:checked', this).forEach((box) => items.push({ id: Number(box.value), quantity: 1 }));
      const error = $('[data-bundle-error]', this);
      error.hidden = true;
      this.button.disabled = true;
      this.button.textContent = P.strings.adding;
      try {
        await addToCart(items);
      } catch (err) {
        error.textContent = err.message || P.strings.error;
        error.hidden = false;
      } finally {
        this.button.disabled = false;
        this.update();
      }
    }
  }
  customElements.define('product-bundle', ProductBundle);

  /* ---------------- Delivery estimate (business days) ---------------- */
  $$('[data-delivery]').forEach((el) => {
    const addBusinessDays = (date, days) => {
      const d = new Date(date);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) added += 1;
      }
      return d;
    };
    const fmt = (d) => `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    const now = new Date();
    const out = $('[data-delivery-range]', el);
    out.textContent = `od ${fmt(addBusinessDays(now, Number(el.dataset.min)))} do ${fmt(addBusinessDays(now, Number(el.dataset.max)))}`;

    const cutoff = Number(el.dataset.cutoff);
    const line = $('[data-cutoff-line]', el);
    if (!cutoff || !line) return;
    const tick = () => {
      const t = new Date();
      const end = new Date(t);
      end.setHours(cutoff, 0, 0, 0);
      const weekday = t.getDay() !== 0 && t.getDay() !== 6;
      const left = end - t;
      if (!weekday || left <= 0) { line.hidden = true; return false; }
      const h = Math.floor(left / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      $('[data-cutoff-time]', line).textContent = h > 0 ? `${h} h ${m} min` : `${m} min`;
      line.hidden = false;
      return true;
    };
    if (tick()) {
      const timer = setInterval(() => { if (!tick()) clearInterval(timer); }, 30000);
    }
  });

  /* ---------------- UGC videos ---------------- */
  const ugcVideos = $$('[data-ugc-card] video');
  if (ugcVideos.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          target.preload = 'auto';
          const p = target.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          target.pause();
        }
      });
    }, { threshold: 0.5 });
    ugcVideos.forEach((v) => { v.muted = true; v.playsInline = true; io.observe(v); });

    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-ugc-card]');
      if (!card) return;
      const video = $('video', card);
      if (!video) return;
      const willUnmute = video.muted;
      // Only one video with sound at a time.
      ugcVideos.forEach((v) => { if (v !== video) { v.muted = true; syncSoundIcon(v); } });
      video.muted = !willUnmute;
      if (video.paused) video.play().catch(() => {});
      syncSoundIcon(video);
    });
  }
  function syncSoundIcon(video) {
    const btn = $('[data-ugc-sound]', video.closest('[data-ugc-card]'));
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(!video.muted));
    $('[data-icon-off]', btn).hidden = !video.muted;
    $('[data-icon-on]', btn).hidden = video.muted;
  }

  /* ---------------- Product recommendations (async) ---------------- */
  class ProductRecs extends HTMLElement {
    connectedCallback() {
      const io = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        try {
          const html = await fetch(this.dataset.url).then((r) => r.text());
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recs');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          else this.closest('section').hidden = true;
        } catch (e) {
          this.closest('section').hidden = true;
        }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recs', ProductRecs);

  /* ---------------- Newsletter popup ---------------- */
  class NewsPopup extends HTMLElement {
    connectedCallback() {
      this.key = 'mv-popup-dismissed';
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-popup-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      // After a successful sign-up the page reloads: show the code right away.
      if ($('[data-popup-success]', this) || $('.form-error', this)) return this.open(true);
      if (this.hasAttribute('data-design-mode') || this.dismissed()) return;
      this.timer = setTimeout(() => this.open(), Number(this.dataset.delay || 12) * 1000);
      if (this.dataset.exit === 'true' && window.matchMedia('(pointer: fine)').matches) {
        this.onLeave = (e) => { if (e.clientY <= 0) this.open(); };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    dismissed() {
      try {
        const until = Number(localStorage.getItem(this.key));
        return until && until > Date.now();
      } catch (e) { return false; }
    }

    open(force) {
      if (!force && (this.shown || this.dismissed() || document.querySelector('cart-drawer.is-open'))) return;
      this.shown = true;
      clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      const input = $('input[type="email"]', this);
      setTimeout(() => (input || $('.popup__close', this)).focus({ preventScroll: true }), 50);
    }

    close() {
      this.hidden = true;
      document.body.style.overflow = '';
      try { localStorage.setItem(this.key, String(Date.now() + Number(this.dataset.days || 7) * 86400000)); } catch (e) { /* storage blocked */ }
    }
  }
  customElements.define('news-popup', NewsPopup);

  /* ---------------- Copy discount code ---------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn || !navigator.clipboard) return;
    navigator.clipboard.writeText(btn.dataset.copy).then(() => {
      const hint = $('span', btn);
      if (hint) hint.textContent = P.strings.copied;
    }).catch(() => {});
  });

  /* ---------------- Collection sort ---------------- */
  $$('[data-sort]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });

  /* ---------------- Desktop dropdowns: one open at a time ---------------- */
  const drops = $$('.nav-drop__details');
  drops.forEach((d) => d.addEventListener('toggle', () => {
    if (d.open) drops.forEach((o) => { if (o !== d) o.open = false; });
  }));
  document.addEventListener('click', (e) => {
    drops.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') drops.forEach((d) => { d.open = false; });
  });

  /* ---------------- Reviews: load more ---------------- */
  $$('[data-reviews-more]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const grid = btn.closest('.reviews').querySelector('[data-reviews]');
      const hidden = $$('[data-review-hidden][hidden]', grid);
      hidden.slice(0, Number(btn.dataset.step) || 9).forEach((el) => { el.hidden = false; });
      if (hidden.length <= (Number(btn.dataset.step) || 9)) btn.hidden = true;
    });
  });
})();

